import { useState } from "react";
import { AlertTriangle, Eye, EyeOff, KeyRound, LogIn, UserPlus } from "lucide-react";
import { Button, Card, Field, Input } from "./ui";
import { api, type StudentDto } from "../lib/api";

export default function StudentAuth({ onSignedIn }: { onSignedIn: (s: StudentDto) => void }) {
  const [mode, setMode] = useState<"signup" | "signin" | "forgot">("signin");
  const [form, setForm] = useState({ fullName: "", facultyNumber: "", contactNumber: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const switchMode = (next: typeof mode) => {
    setMode(next);
    setError(null);
    setNotice(null);
  };

  const requestReset = async () => {
    setError(null);
    setNotice(null);
    if (!form.facultyNumber.trim()) {
      setError("Enter your faculty number so we can find your account.");
      return;
    }
    setBusy(true);
    try {
      const res = await api.forgotPassword(form.facultyNumber.trim());
      setNotice(res.message);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setError(null);
    if (mode === "signup" && form.password.length < 10) {
      setError("Choose a password with at least 10 characters.");
      return;
    }
    setBusy(true);
    try {
      const res = mode === "signup"
        ? await api.studentSignup(form)
        : await api.studentSignin(form.facultyNumber, form.password);
      onSignedIn(res.student);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-6 md:p-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-xl font-semibold text-white">
            {mode === "signup" ? "Student sign up" : mode === "signin" ? "Student sign in" : "Reset your password"}
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-white/50">
            {mode === "signup"
              ? "Create your account with four details. You can complete your student and event-planning profile on the voting page."
              : mode === "signin"
                ? "Sign in with your faculty number and password."
                : "Enter your faculty number. We'll send a password reset link to your registered number on WhatsApp within 60 seconds."}
          </p>
        </div>
        {mode !== "forgot" && (
          <button
            type="button"
            onClick={() => switchMode(mode === "signup" ? "signin" : "signup")}
            className="rounded-full border border-white/15 px-4 py-2 text-sm text-white/70 transition-colors hover:border-white/35 hover:text-white"
          >
            {mode === "signup" ? "Already registered? Sign in" : "New here? Sign up"}
          </button>
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        {mode === "signup" && (
          <Field label="Full name" htmlFor="sa-name">
            <Input id="sa-name" value={form.fullName} onChange={set("fullName")} placeholder="As per university records" autoComplete="name" />
          </Field>
        )}
        <Field label="Faculty number" htmlFor="sa-faculty">
          <Input id="sa-faculty" value={form.facultyNumber} onChange={set("facultyNumber")} autoComplete="off" />
        </Field>
        {mode === "signup" && (
          <Field label="Phone number" htmlFor="sa-phone">
            <Input id="sa-phone" type="tel" value={form.contactNumber} onChange={set("contactNumber")} autoComplete="tel" />
          </Field>
        )}
        {mode !== "forgot" && (
          <Field label="Password" htmlFor="sa-password">
            <div className="relative">
              <Input
                id="sa-password"
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={set("password")}
                autoComplete={mode === "signup" ? "new-password" : "current-password"}
                minLength={mode === "signup" ? 10 : undefined}
                className="pr-12"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-1 text-white/50 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#AFDDFF]/50"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {mode === "signup" && <p className="mt-1 text-xs text-white/40">At least 10 characters.</p>}
          </Field>
        )}
      </div>

      {notice && (
        <p className="mt-4 rounded-md border border-[#AFDDFF]/30 bg-[#AFDDFF]/10 px-3 py-2 text-sm text-[#AFDDFF]" role="status">
          {notice}
        </p>
      )}
      {error && (
        <p className="mt-4 flex items-start gap-2 text-sm text-amber-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />{error}
        </p>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-4">
        {mode === "forgot" ? (
          <>
            <Button onClick={() => void requestReset()} disabled={busy}>
              <KeyRound className="h-4 w-4" aria-hidden="true" />
              {busy ? "Sending request…" : "Send reset link"}
            </Button>
            <button
              type="button"
              onClick={() => switchMode("signin")}
              className="text-sm text-white/60 underline-offset-4 transition-colors hover:text-white hover:underline"
            >
              Back to sign in
            </button>
          </>
        ) : (
          <>
            <Button onClick={() => void submit()} disabled={busy}>
              {mode === "signup" ? <UserPlus className="h-4 w-4" aria-hidden="true" /> : <LogIn className="h-4 w-4" aria-hidden="true" />}
              {busy ? "Please wait…" : mode === "signup" ? "Create my account & continue" : "Sign in & continue"}
            </Button>
            {mode === "signin" && (
              <button
                type="button"
                onClick={() => switchMode("forgot")}
                className="text-sm text-white/60 underline-offset-4 transition-colors hover:text-[#AFDDFF] hover:underline"
              >
                Forgot password?
              </button>
            )}
          </>
        )}
      </div>
      <p className="mt-4 text-xs leading-relaxed text-white/45">
        Your phone number is visible only to the organizing committee. Never reuse a password from another account.
      </p>
    </Card>
  );
}
